class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5166/api';
    this.authToken = null;
  }

  async request(endpoint, options = {}) {
    const { skipAuth = false, headers = {}, body, ...rest } = options;
    const requestHeaders = {
      Accept: 'application/json, text/plain, */*',
      ...headers,
      ...(skipAuth ? {} : this.getAuthHeaders())
    };

    let requestBody = body;
    if (requestBody && typeof requestBody === 'object' && !(requestBody instanceof FormData)) {
      requestHeaders['Content-Type'] = 'application/json; charset=utf-8';
      requestBody = JSON.stringify(requestBody);
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...rest,
      headers: requestHeaders,
      body: requestBody
    });

    const payload = await this.parseResponse(response);

    if (!response.ok) {
      if (response.status === 401 && !skipAuth) {
        this.clearAuthCookies();
      }
      const error = new Error(this.getErrorMessage(payload, response));
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    if (payload && typeof payload.ok !== 'undefined' && !payload.ok) {
      const error = new Error(this.getErrorMessage(payload, response));
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  }

  get(endpoint, headers = {}) {
    return this.request(endpoint, { method: 'GET', headers });
  }

  post(endpoint, data, headers = {}) {
    return this.request(endpoint, { method: 'POST', body: data, headers });
  }

  postPublic(endpoint, data, headers = {}) {
    return this.request(endpoint, { method: 'POST', body: data, headers, skipAuth: true });
  }

  put(endpoint, data, headers = {}) {
    return this.request(endpoint, { method: 'PUT', body: data, headers });
  }

  delete(endpoint, headers = {}) {
    return this.request(endpoint, { method: 'DELETE', headers });
  }

  async download(endpoint, fileName, headers = {}) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: {
        ...headers,
        ...this.getAuthHeaders()
      }
    });

    if (!response.ok) {
      const payload = await this.parseResponse(response);
      if (response.status === 401) {
        this.clearAuthCookies();
      }
      throw new Error(this.getErrorMessage(payload, response));
    }

    const blob = await response.blob();
    const downloadName = fileName || this.getFileNameFromDisposition(response.headers.get('content-disposition')) || 'descarga';
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  setAuthToken(token) {
    this.authToken = token || null;
  }

  getAuthHeaders() {
    const token = this.authToken || this.getTokenFromCookies();
    if (token && !this.authToken) {
      this.authToken = token;
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  getTokenFromCookies() {
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith('auth_token='));

    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : null;
  }

  clearAuthCookies() {
    document.cookie = 'auth_token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie = 'user_data=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    this.authToken = null;
  }

  async parseResponse(response) {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  getErrorMessage(payload, response) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload?.mensaje) return payload.mensaje;
    if (payload?.message) return payload.message;
    if (payload?.error) return payload.error;
    if (payload?.title) return payload.title;
    return `HTTP Error: ${response.status} ${response.statusText || ''}`.trim();
  }

  getFileNameFromDisposition(disposition) {
    if (!disposition) return '';
    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/"/g, ''));
    const match = disposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || '';
  }
}

const apiService = new ApiService();
export default apiService;
